import {
  Box,
  Button,
  Card,
  CircularProgress,
  Grid,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useTheme,
  Chip,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import getAxiosInstance from 'api';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DateRangePicker } from 'rsuite';
import LeftArrow from '../../assets/images/svg/left-arrow.svg';
import toast from 'react-hot-toast';
import { getDateRangeFromQuickOption } from 'utils/common';
import ReactApexChart from 'react-apexcharts';
import SearchIcon from '@mui/icons-material/Search';

// Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
  padding: '24px',
  boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.06)',
  borderRadius: '12px',
  border: '1px solid rgba(0, 0, 0, 0.1)',
  backgroundColor: '#FFFFFF',
}));

const KPICard = styled(Card)(({ theme }) => ({
  padding: '24px',
  boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.06)',
  borderRadius: '12px',
  border: '1px solid rgba(0, 0, 0, 0.1)',
  backgroundColor: '#FFFFFF',
  textAlign: 'center',
  height: '100%',
}));

const StyledTableCell = styled(TableCell)(() => ({
  backgroundColor: '#FFFFFF',
  border: '1px solid #DEDEDE',
  color: '#212120',
  fontSize: '14px',
  fontWeight: '500',
}));

const StyledTableCellHead = styled(TableCell)(() => ({
  backgroundColor: '#F0F0F0',
  color: 'rgba(33, 33, 32, 0.60)',
  border: '1px solid #DEDEDE',
  fontSize: '14px',
  fontWeight: '600',
}));

const BackButton = styled(Button)(() => ({
  backgroundColor: '#fff',
  color: '#000',
  borderRadius: '50%',
  padding: '4px',
  minWidth: '32px',
  height: '32px',
  '&:hover': { backgroundColor: '#f5f5f5' },
}));

const quickRanges = [
  { label: 'Current Day', value: 'day' },
  { label: 'Current Week', value: 'week' },
  { label: 'Current Month', value: 'month' },
  { label: 'Last 3 Months', value: '3months' },
  { label: 'Last 6 Months', value: '6months' },
  { label: 'Last 12 Months', value: '12months' },
  { label: 'Quarter to date', value: 'quarter' },
  { label: 'All Time', value: 'all' },
];

const StatusChip = styled(Chip)(({ status }) => ({
  backgroundColor:
    status === 'Successful'
      ? '#E8F5E9'
      : status === 'Failed'
      ? '#FFEBEE'
      : '#FFF3E0',
  color:
    status === 'Successful'
      ? '#2E7D32'
      : status === 'Failed'
      ? '#C62828'
      : '#E65100',
  fontWeight: 500,
  fontSize: '12px',
  borderRadius: '16px',
  height: '24px',
}));

const KYCDetails = ({ routeKycType = '' }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [selectedDate, setSelectedDate] = useState([
    moment().startOf('month').toDate(),
    moment().toDate(),
  ]);

  const [kycCounts, setKycCounts] = useState({
    total: 0,
    successful: 0,
    failed: 0,
    blocked: 0,
  });
  const [loadingCounts, setLoadingCounts] = useState(false);

  const [pieChartData, setPieChartData] = useState([]);
  const [loadingChart, setLoadingChart] = useState(false);
  const [pieConfig, setPieConfig] = useState({
    series: [],
    options: {
      chart: { type: 'pie' },
      labels: [],
      legend: { position: 'bottom' },
      theme: {
        monochrome: {
          enabled: true,
          color: '#FF6543',
          shadeTo: 'light',
          shadeIntensity: 0.65,
        },
      },
      responsive: [
        {
          breakpoint: 768,
          options: { chart: { width: '100%' }, legend: { position: 'bottom' } },
        },
      ],
    },
  });
  const [partnerIds, setPartnerIds] = useState([]);
  const [selectedPartnerId, setSelectedPartnerId] = useState(null);

  const [kycDetails, setKycDetails] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [order, setOrder] = useState('DESC');
  const [orderBy, setOrderBy] = useState('createdAt');
  const [quickRangeValue, setQuickRangeValue] = useState('');

  useEffect(() => {
    fetchKYCCounts();
    fetchPieChartData();
  }, [selectedDate, routeKycType]);

  useEffect(() => {
    fetchKYCDetails();
  }, [
    selectedDate,
    page,
    limit,
    selectedPartnerId,
    search,
    order,
    orderBy,
    routeKycType,
  ]);

  const fetchKYCCounts = () => {
    setLoadingCounts(true);
    getAxiosInstance(
      `adminOrchestrator/dashboard/getKYCRequestStatusCounts`,
      'post',
      {
        kycType: routeKycType || 'ALL',
        startDate: moment(selectedDate[0]).format('MM-DD-YYYY'),
        endDate: moment(selectedDate[1]).format('MM-DD-YYYY'),
      },
      { 'journey-type': 'Dashboard' },
    )
      .then((res) => {
        if (res?.data?.codes === 200) {
          const data = res.data.data;
          const total =
            (data.successKYCRequest || 0) +
            (data.failedKYCRequestCount || 0) +
            (data.blockedKYCRequestCount || 0);
          setKycCounts({
            total: total,
            successful: data.successKYCRequest || 0,
            failed: data.failedKYCRequestCount || 0,
            blocked: data.blockedKYCRequestCount || 0,
          });
        }
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.message || 'Failed to fetch KYC counts',
        );
      })
      .finally(() => setLoadingCounts(false));
  };

  const fetchPieChartData = () => {
    setLoadingChart(true);
    getAxiosInstance(
      `adminOrchestrator/dashboard/getKYCCountsGroupedByBP`,
      'post',
      {
        kycType: routeKycType || 'ALL',
        startDate: moment(selectedDate[0]).format('MM-DD-YYYY'),
        endDate: moment(selectedDate[1]).format('MM-DD-YYYY'),
      },
      { 'journey-type': 'Dashboard' },
    )
      .then((res) => {
        if (res?.data?.codes === 200) {
          const responseData = res.data?.data || [];

          if (responseData.length === 0) {
            setPartnerIds([]);
            setPieConfig({
              series: [0],
              options: {
                ...pieConfig.options,
                labels: ['No Data'],
              },
            });
            return;
          }

          const labels = responseData.map(
            (item) => item.companyName || `Company ${item.businessPartnerId}`,
          );
          const series = responseData.map((item) => parseInt(item.kycCount));
          const ids = responseData.map((item) => item.businessPartnerId);

          setPartnerIds(ids);
          setPieChartData(responseData);
          setPieConfig({
            series,
            options: {
              ...pieConfig.options,
              labels,
              chart: {
                type: 'pie',
                events: {
                  dataPointSelection: (event, chartContext, config) => {
                    const clickedIndex = config.dataPointIndex;
                    const partnerId = ids[clickedIndex];
                    setSelectedPartnerId(partnerId);
                  },
                },
              },
            },
          });
        }
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.message || 'Failed to fetch chart data',
        );
      })
      .finally(() => setLoadingChart(false));
  };

  const fetchKYCDetails = () => {
    setLoadingDetails(true);
    const payload = {
      startDate: moment(selectedDate[0]).format('MM-DD-YYYY'),
      endDate: moment(selectedDate[1]).format('MM-DD-YYYY'),
      page: page + 1,
      limit: limit,
      sortBy: orderBy,
      sortDirection: order,
      ...(selectedPartnerId && { businessPartnerId: selectedPartnerId }),
      ...(search && { search: search }),
      ...(routeKycType && { kycType: routeKycType }),
    };

    getAxiosInstance(
      `adminOrchestrator/dashboard/getKYCByDateRange`,
      'post',
      payload,
      { 'journey-type': 'Dashboard' },
    )
      .then((res) => {
        if (res?.data?.codes === 200) {
          setKycDetails(res?.data?.data?.items || []);
          setTotal(res?.data?.data?.total || 0);
        }
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.message || 'Failed to fetch KYC details',
        );
      })
      .finally(() => setLoadingDetails(false));
  };

  const activeCompanies = pieChartData.length;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        mb={3}
        gap={2}
      >
        <Box display="flex" alignItems="center">
          <BackButton onClick={() => navigate(-1)}>
            <Box component="img" src={LeftArrow} />
          </BackButton>
          <Box ml={2}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 600,
                color: '#212120',
                fontSize: { xs: '24px', md: '32px' },
              }}
            >
              {`${routeKycType} Dashboard`}
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
              Track verification calls by company and product type.
            </Typography>
          </Box>
        </Box>
        <Box
          display="flex"
          alignItems="center"
          gap={2}
          sx={{
            flexDirection: { xs: 'column', sm: 'row' },
            width: { xs: '100%', md: 'auto' },
          }}
        >
          <FormControl
            size="small"
            sx={{ minWidth: { xs: '100%', sm: '150px' } }}
          >
            <Select
              value={quickRangeValue || ''}
              onChange={(e) => {
                const value = e.target.value;
                setQuickRangeValue(value);
                if (value) {
                  const range = getDateRangeFromQuickOption(value);
                  if (range) setSelectedDate([range.start, range.end]);
                }
              }}
              displayEmpty
              sx={{
                backgroundColor: '#fff',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0, 0, 0, 0.23)',
                },
              }}
            >
              <MenuItem value="">Quick Range</MenuItem>
              {quickRanges.map((range) => (
                <MenuItem key={range.value} value={range.value}>
                  {range.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <DateRangePicker
            appearance="default"
            placeholder="Select Date Range"
            value={selectedDate}
            onChange={(value) => setSelectedDate(value)}
            showOneCalendar
            style={{ width: '100%' }}
          />
        </Box>
      </Stack>

      {/* KPI Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard>
            <Typography
              variant="body2"
              sx={{ color: '#666', mb: 1, fontSize: '14px' }}
            >
              Total KYC Calls
            </Typography>
            {loadingCounts ? (
              <CircularProgress size={24} />
            ) : (
              <Typography
                variant="h4"
                sx={{ fontWeight: 600, color: '#212120' }}
              >
                {kycCounts.total.toLocaleString()}
              </Typography>
            )}
          </KPICard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard>
            <Typography
              variant="body2"
              sx={{ color: '#666', mb: 1, fontSize: '14px' }}
            >
              Successful Calls
            </Typography>
            {loadingCounts ? (
              <CircularProgress size={24} />
            ) : (
              <Typography
                variant="h4"
                sx={{ fontWeight: 600, color: '#212120' }}
              >
                {kycCounts.successful.toLocaleString()}
              </Typography>
            )}
          </KPICard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard>
            <Typography
              variant="body2"
              sx={{ color: '#666', mb: 1, fontSize: '14px' }}
            >
              Failed Calls
            </Typography>
            {loadingCounts ? (
              <CircularProgress size={24} />
            ) : (
              <Typography
                variant="h4"
                sx={{ fontWeight: 600, color: '#212120' }}
              >
                {kycCounts.failed.toLocaleString()}
              </Typography>
            )}
          </KPICard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard>
            <Typography
              variant="body2"
              sx={{ color: '#666', mb: 1, fontSize: '14px' }}
            >
              Active Companies
            </Typography>
            {loadingChart ? (
              <CircularProgress size={24} />
            ) : (
              <Typography
                variant="h4"
                sx={{ fontWeight: 600, color: '#212120' }}
              >
                {activeCompanies}
              </Typography>
            )}
          </KPICard>
        </Grid>
      </Grid>

      {/* Pie Chart Section */}
      <StyledCard sx={{ mb: 3, width: '100%' }}>
        <Typography
          variant="h5"
          sx={{ fontWeight: 600, color: '#212120', mb: 2 }}
        >
          {`${routeKycType} Calls by Company`}
        </Typography>
        {loadingChart ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              height: '400px',
              alignItems: 'center',
            }}
          >
            <CircularProgress color="secondary" />
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              width: '100%',
            }}
          >
            <ReactApexChart
              options={pieConfig.options}
              series={pieConfig.series}
              type="pie"
              height={350}
              width="100%"
            />
          </Box>
        )}
        {selectedPartnerId !== null && (
          <Box mt={2} display="flex" justifyContent="center">
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => setSelectedPartnerId(null)}
              size="small"
            >
              Reset Filter
            </Button>
          </Box>
        )}
      </StyledCard>

      {/* KYC Call Details Table */}
      <StyledCard>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          mb={2}
          sx={{ flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}
        >
          <Box>
            <Typography
              variant="h5"
              sx={{ fontWeight: 600, color: '#212120', mb: 0.5 }}
            >
              {`${routeKycType} Call Details`}
            </Typography>
            <Typography variant="body2" sx={{ color: '#666' }}>
              View and manage all individual verification calls.
            </Typography>
          </Box>
          <Box
            display="flex"
            gap={2}
            sx={{
              flexDirection: { xs: 'column', sm: 'row' },
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            <TextField
              placeholder="Search calls..."
              variant="outlined"
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: '#999' }} />,
              }}
              sx={{ width: { xs: '100%', sm: '250px' } }}
            />
          </Box>
        </Box>

        {/* Table */}
        {loadingDetails ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '200px',
            }}
          >
            <CircularProgress color="secondary" />
          </Box>
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 750 }}>
              <TableHead>
                <TableRow>
                  <StyledTableCellHead>Email Address</StyledTableCellHead>
                  <StyledTableCellHead>User Name</StyledTableCellHead>
                  <StyledTableCellHead>Company Name</StyledTableCellHead>
                  <StyledTableCellHead>Product Type</StyledTableCellHead>
                  <StyledTableCellHead>Product Name</StyledTableCellHead>
                  <StyledTableCellHead>Status</StyledTableCellHead>
                  <StyledTableCellHead>Call Date</StyledTableCellHead>
                </TableRow>
              </TableHead>
              <TableBody>
                {kycDetails.length > 0 ? (
                  kycDetails.map((row, index) => (
                    <TableRow hover key={row.bvnConsentId || index}>
                      <StyledTableCell>{row.email || 'N/A'}</StyledTableCell>
                      <StyledTableCell>
                        {row.email?.split('@')[0] || 'N/A'}
                      </StyledTableCell>
                      <StyledTableCell>
                        {row.companyName || 'N/A'}
                      </StyledTableCell>
                      <StyledTableCell>{row.kycType || 'N/A'}</StyledTableCell>
                      <StyledTableCell>Basic</StyledTableCell>
                      <StyledTableCell>
                        <StatusChip
                          label={row.status || 'Pending'}
                          status={
                            row.status === 'Active'
                              ? 'Successful'
                              : row.status || 'Pending'
                          }
                        />
                      </StyledTableCell>
                      <StyledTableCell>
                        {row.createdAt
                          ? moment(row.createdAt).format('YYYY-MM-DD')
                          : 'N/A'}
                      </StyledTableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <StyledTableCell
                      colSpan={7}
                      sx={{ textAlign: 'center', py: 4 }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        No KYC call details found
                      </Typography>
                    </StyledTableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={total}
          rowsPerPage={limit}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setLimit(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </StyledCard>
    </Box>
  );
};

export default KYCDetails;

